import React, { useState, useCallback, useRef } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { getAllStudents } from '../../api/sponteAPI';
import { anoLetivo } from '../../utils/YEARCONSTS';
import { Loading } from '../Loading/Loading';

/**
 * Componente para buscar e selecionar um aluno
 * Carrega a lista apenas quando o usuário interage com o campo
 */
export const StudentSearch = ({ value, onChange, error, helperText }) => {
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [uniqueStudentNames, setUniqueStudentNames] = useState([]);
  const hasLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);

  const loadStudents = useCallback(async () => {
    // Evitar carregar múltiplas vezes
    if (hasLoadedRef.current || isLoadingRef.current) {
      return;
    }

    try {
      isLoadingRef.current = true;
      setLoading(true);
      const res = await getAllStudents(anoLetivo);
      if (!res || !Array.isArray(res)) {
        console.error('Resposta inválida de getAllStudents:', res);
        setUniqueStudentNames([]);
        return;
      }
      // Extrair dados do aluno, tratando tanto string quanto array (formato XML parseado)
      // EXATAMENTE como na tela de documentos
      const names = res.map((item) => {
        const alunoID = Array.isArray(item.AlunoID) ? item.AlunoID[0] : item.AlunoID;
        const aluno = Array.isArray(item.Aluno) ? item.Aluno[0] : item.Aluno;
        // Garantir que são strings
        const alunoIDStr = typeof alunoID === 'string' || typeof alunoID === 'number' ? String(alunoID) : '';
        const alunoStr = typeof aluno === 'string' ? aluno : String(aluno || '');
        return `${alunoIDStr}-${alunoStr}`;
      }).filter(name => name && name !== '-'); // Remover valores inválidos
      const uniqueNames = [...new Set(names)];
      setUniqueStudentNames(uniqueNames);
      hasLoadedRef.current = true;
    } catch (error) {
      console.error("Erro ao carregar estudantes:", error);
      setUniqueStudentNames([]);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, []);

  const handleInputChange = useCallback((event, newInputValue) => {
    setSearchValue(newInputValue);
    // Carregar estudantes quando o usuário começar a digitar
    if (newInputValue && !hasLoadedRef.current && !isLoadingRef.current) {
      loadStudents();
    }
  }, [loadStudents]);

  const handleFocus = useCallback(() => {
    // Carregar estudantes quando o campo receber foco
    if (!hasLoadedRef.current && !isLoadingRef.current) {
      loadStudents();
    }
  }, [loadStudents]);

  const handleStudentSelect = useCallback(
    (event, newValue) => {
      // Converter string "ID-Nome" para objeto { idSponte, name } para compatibilidade
      if (newValue && typeof newValue === 'string') {
        const [idSponte, ...nameParts] = newValue.split('-');
        const name = nameParts.join('-');
        onChange({ idSponte, name, id: idSponte });
      } else {
        onChange(newValue);
      }
    },
    [onChange]
  );

  const filteredStudents = uniqueStudentNames.filter((name) =>
    name.toLowerCase().includes(searchValue.toLowerCase())
  );

  // Converter value (objeto) para string (formato "ID-Nome") se necessário
  const displayValue = value 
    ? (typeof value === 'string' ? value : `${value.idSponte}-${value.name}`)
    : null;

  return (
    <Autocomplete
      onChange={handleStudentSelect}
      disablePortal
      options={filteredStudents}
      value={displayValue}
      inputValue={searchValue}
      onInputChange={handleInputChange}
      filterOptions={(options) => options} // Desabilitar filtro padrão, usar nosso filtro manual
      getOptionLabel={(option) => option || ''}
      isOptionEqualToValue={(option, value) => option === value}
      renderOption={(props, option) => (
        <li {...props} key={option}>
          {option}
        </li>
      )}
      sx={{ width: "100%" }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Digite parte do nome do aluno para consulta"
          fullWidth
          variant="outlined"
          error={error}
          helperText={helperText}
          onFocus={handleFocus}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <Loading size="small" /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

